import { forwardRef, useId, type InputHTMLAttributes } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label text (also wires up the accessible name). */
  label: string;
  /** A second, quieter line under the label. */
  hint?: string;
}

/**
 * A labelled tick box.
 *
 * The whole label is the tap target (at least 40px tall), and the box takes the
 * accent colour when ticked.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, id, className = '', ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const labelId = `${inputId}-label`;
  const hintId = `${inputId}-hint`;

  return (
    <label htmlFor={inputId} className={`flex min-h-[40px] cursor-pointer items-start gap-x-3 py-1.5 ${className}`.trim()}>
      <input
        id={inputId}
        ref={ref}
        type="checkbox"
        // The name is the label line alone: a hint under it describes, it does not name.
        aria-labelledby={labelId}
        aria-describedby={hint ? hintId : undefined}
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded-sm border-[var(--color-border-control)] text-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/30"
        {...rest}
      />
      <span className="min-w-0">
        <span id={labelId} className="block text-sm text-[var(--color-text)]">
          {label}
        </span>
        {hint && (
          <span id={hintId} className="block text-xs text-[var(--color-text-secondary)]">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
});
